/**
 * Asset specs for the antenna family: the shared bindings, subviews and signal
 * paths of the rf_antenna element, and a parametric spec for any NOAA antenna
 * record so every one of the 41 antennas has a correctly sized twin.
 */
import type { Antenna } from '@/features/loader/types';
import { antennaSite } from '@/features/catalogue/facilities';
import { estimateAntennaBounds } from '../procedural/dimensions';
import { DEFAULT_FOV, fitDistance, orbitPose } from '../scene/fit';
import type {
  AssetSpec,
  CameraPose,
  NodeBinding,
  PedestalType,
  ProceduralAntennaSpec,
  SignalPathSpec,
  SubviewSpec,
} from '../types';

/** Unity initialPOV of the RF antenna family in actionable_items.json. */
export const ANTENNA_POV = { azimuthDeg: 212.6765, elevationDeg: 18.73539 };

/** rf_antenna subsystem ids in element order; the generator names a node for each. */
export const RF_ANTENNA_IDS = [
  'physical_components',
  'main_antenna',
  'antenna_control_unit',
  'acu_cooling_fan',
  'beacon_receiver',
  'tracking_processor',
  'backup_structure',
  'drive_system',
  'feed_horn',
  'low_noise_amplifier',
  'pedestal',
  'primary_reflector',
  'waveguide',
  'container_building',
  'spectrum_analyzer',
  'freq_translator',
  'ifl_switch',
  'modem',
  'raw_data_modem',
  'cda_modem',
  'grb_modem',
  'bit_synchronizer',
  'raw_data_fep',
  'tc_fep',
  'grb_fep',
  'decoder',
  'if_distribution',
  'switch_matrix',
  'storage_servers',
  'environmental',
  'pointing_status',
  'rf_performance',
  'system_health',
] as const;

/** Receive chain in the order the HUD draws it (feed to storage); transmit is the reverse. */
export const RECEIVE_CHAIN = [
  'feed_horn',
  'low_noise_amplifier',
  'waveguide',
  'freq_translator',
  'if_distribution',
  'ifl_switch',
  'modem',
  'bit_synchronizer',
  'decoder',
  'switch_matrix',
  'storage_servers',
];

export function antennaSignalPaths(): SignalPathSpec[] {
  return [
    {
      id: 'receive',
      label: 'Receive chain',
      direction: 'receive',
      subsystemIds: [...RECEIVE_CHAIN],
      colour: '#deff5f',
    },
    {
      id: 'transmit',
      label: 'Transmit chain',
      direction: 'transmit',
      subsystemIds: [...RECEIVE_CHAIN].reverse(),
      colour: '#7fc4ff',
    },
  ];
}

export function antennaSubviews(): SubviewSpec[] {
  return [
    {
      id: 'contInterior',
      label: 'Container',
      procedural: 'container-interior',
      pose: { position: [0.55, 1.55, 2.35], target: [-0.7, 1.15, -0.6], fov: 60 },
    },
    {
      id: 'roomInterior',
      label: 'Lower Room',
      procedural: 'lower-room',
      pose: { position: [2.3, 1.7, 2.3], target: [-0.4, 1.1, -1.2], fov: 60 },
    },
  ];
}

/** Bindings for the full rf_antenna element (33 ids, generator node of the same name). */
export function rfAntennaBindings(): NodeBinding[] {
  return RF_ANTENNA_IDS.map((id) => ({ subsystemId: id, nodes: [id] }));
}

/** Ids of the four live cards an RFIMS site element starts with (scripts/elements/build-rfims.mjs). */
export const RFIMS_LIVE_CARD_IDS = ['environmental', 'host_health', 'network', 'unit_info'] as const;

/**
 * Bindings of the four live cards on the antenna model. environmental is the
 * cabinet's Falcon-200 card (temperature, humidity, door, leak, relay): the
 * container shell and HVAC outside, the monitoring unit's sensor and the HVAC
 * inlet in the container subview. host_health is the monitoring unit,
 * network the cable entry (the cable tray inside), unit_info the unit's LED.
 */
export function rfimsLiveBindings(): NodeBinding[] {
  return [
    { subsystemId: 'environmental', nodes: ['shell', 'hvac', 'hvac_grille', 'unit_sensor', 'hvac_inlet'] },
    { subsystemId: 'host_health', nodes: ['monitoring_unit'] },
    { subsystemId: 'network', nodes: ['cable_entry', 'cable_tray'] },
    { subsystemId: 'unit_info', nodes: ['monitoring_led', 'unit_led'] },
  ];
}

/**
 * Bindings for an RFIMS site element, an RF antenna site with a monitoring
 * unit: the live cards first, then the rf_antenna bindings for the antenna
 * tree except environmental, whose id the live card owns on these sites.
 */
export function rfimsBindings(): NodeBinding[] {
  return [...rfimsLiveBindings(), ...rfAntennaBindings().filter((b) => b.subsystemId !== 'environmental')];
}

export function antennaRig(antenna: Antenna): ProceduralAntennaSpec['rig'] {
  const type = (antenna.capabilities.type ?? '').toLowerCase();
  const bands = antenna.capabilities.bands.map((b) => b.toLowerCase());
  if (type.includes('phased') || type.includes('array')) return 'phased-array';
  if (type.includes('optical') || bands.includes('optical')) return 'optical';
  const d = antenna.capabilities.diameterM ?? 0;
  const highBand = bands.some((b) => b.startsWith('ka') || b.startsWith('x'));
  return highBand && d >= 7 ? 'cassegrain' : 'prime-focus';
}

export function antennaPedestal(diameterM: number): PedestalType {
  if (diameterM >= 18) return 'wheel-and-track';
  if (diameterM >= 9) return 'king-post';
  if (diameterM >= 4.5) return 'turret';
  if (diameterM >= 2.5) return 'tripod';
  return 'tower';
}

/** Polar sites and small Ka-band terminals are radome-enclosed. */
export function antennaRadome(antenna: Antenna): boolean {
  const d = antenna.capabilities.diameterM ?? 0;
  const ka = antenna.capabilities.bands.some((b) => b.toLowerCase().startsWith('ka'));
  return Math.abs(antenna.location.lat) >= 70 || (ka && d <= 5);
}

export function proceduralSpecForAntenna(antenna: Antenna): ProceduralAntennaSpec {
  const diameterM = antenna.capabilities.diameterM ?? 4;
  return {
    kind: 'antenna',
    diameterM,
    rig: antennaRig(antenna),
    pedestal: antennaPedestal(diameterM),
    radome: antennaRadome(antenna),
    container: !antenna.capabilities.geoOnly,
  };
}

/** Asset id used for a NOAA antenna twin: `noaa:<antennaId>`; loadAssetSpec resolves it from the loader data. */
export const NOAA_ASSET_PREFIX = 'noaa:';

export function antennaAssetId(antennaId: string, network = 'noaa'): string {
  return `${network}:${antennaId}`;
}

export function assetSpecForAntenna(
  antenna: Antenna,
  network = 'noaa',
): AssetSpec & { procedural: ProceduralAntennaSpec } {
  const procedural = proceduralSpecForAntenna(antenna);
  const { centre, radius } = estimateAntennaBounds(procedural);
  const main: CameraPose = orbitPose(
    centre,
    ANTENNA_POV.azimuthDeg,
    ANTENNA_POV.elevationDeg,
    fitDistance(radius, DEFAULT_FOV),
    DEFAULT_FOV,
  );
  return {
    id: antennaAssetId(antenna.id, network),
    globeLink: { network, antennaId: antenna.id },
    elementId: `antenna_reference:${network}:${antenna.id}`,
    label: antenna.name,
    modelNote: 'Catalogue-sized antenna. Reflector, mount, enclosure and internal equipment are representative. No live subsystem telemetry.',
    procedural,
    poses: { main },
    bindings: rfAntennaBindings(),
    subviews: procedural.container ? antennaSubviews() : undefined,
    signalPaths: antennaSignalPaths(),
    site: antennaSite(antenna, network),
    boundsM: [radius * 2, radius * 1.2, radius * 2],
  };
}
